import * as THREE from 'three';
import { hammeredNormal, normalCanvas, softAlpha } from '../door/door-textures.js';

/**
 * Materials for the 3D HUD pieces: the door's hammered bronze, and faces with a glyph pressed into them. A glyph is drawn from its
 * SVG into a height field, so the light catches its edges like the door's carving instead of a printed icon.
 */

const FACE = 256;

/** Loads an SVG and draws it, black on transparent, centred with a margin, into a square canvas. */
export async function glyphCanvas(url: string, margin: number): Promise<HTMLCanvasElement> {
  // The icon files carry only a viewBox; an image needs a size to decode, so give the markup one.
  const markup = (await (await fetch(url)).text()).replace('<svg ', `<svg width="${FACE}" height="${FACE}" `);
  const source = URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml' })), image = new Image();
  image.src = source; await image.decode().finally(() => URL.revokeObjectURL(source));
  const canvas = document.createElement('canvas'); canvas.width = canvas.height = FACE;
  // Read back twice (`softAlpha`): a canvas kept in memory answers without stalling for the GPU.
  canvas.getContext('2d', { willReadFrequently: true })!.drawImage(image, margin, margin, FACE - margin * 2, FACE - margin * 2);
  return canvas;
}

export interface GlyphFace { map: THREE.CanvasTexture; normalMap: THREE.CanvasTexture; dispose(): void }

/**
 * A round face with the glyph standing proud of it (`raised`) or cut into it. The colour darkens the field and lightens the glyph a
 * little; the normal map does the rest. A shallow bowl toward the rim makes the face read as cast rather than flat.
 */
export async function glyphFace(url: string, field: THREE.ColorRepresentation, ink: THREE.ColorRepresentation, raised = true, margin = .2): Promise<GlyphFace> {
  if (!canDraw()) throw new Error('Glyph faces need a canvas.');
  const glyph = await glyphCanvas(url, FACE * margin), soft = softAlpha(glyph, 2, 2), sharp = softAlpha(glyph, 1, 1);
  const height = new Float32Array(FACE * FACE), albedo = document.createElement('canvas'); albedo.width = albedo.height = FACE;
  const context = albedo.getContext('2d')!, image = context.createImageData(FACE, FACE), base = new THREE.Color(field), top = new THREE.Color(ink), mixed = new THREE.Color();
  for (let y = 0; y < FACE; y++) for (let x = 0; x < FACE; x++) {
    const i = y * FACE + x, r = Math.hypot(x / FACE - .5, y / FACE - .5) * 2;
    height[i] = (raised ? soft[i] : -soft[i]) * .55 - Math.max(0, r - .78) * 1.6;
    // Worn high points: the glyph's top is brighter than its slopes, and the field darkens toward the rim.
    mixed.copy(base).lerp(top, Math.min(1, sharp[i] * 1.15)).multiplyScalar(1 - Math.max(0, r - .6) * .5);
    image.data[i * 4] = mixed.r * 255; image.data[i * 4 + 1] = mixed.g * 255; image.data[i * 4 + 2] = mixed.b * 255; image.data[i * 4 + 3] = 255;
  }
  context.putImageData(image, 0, 0);
  const map = new THREE.CanvasTexture(albedo); map.colorSpace = THREE.SRGBColorSpace;
  const normalMap = new THREE.CanvasTexture(normalCanvas(height, FACE, 9));
  return { map, normalMap, dispose() { map.dispose(); normalMap.dispose(); } };
}

/**
 * A white map and a flat normal map standing in for a glyph face that is still being drawn. A material gets them when it is made, so
 * its shader is the glyph variant from the first frame: swapping the real face in later is a texture change, never a program link.
 * One pair serves every piece; it is a pixel each and is never disposed.
 */
export function blankFace(): { map: THREE.Texture; normalMap: THREE.Texture } {
  if (!blank) {
    const map = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1); map.colorSpace = THREE.SRGBColorSpace; map.needsUpdate = true;
    const normalMap = new THREE.DataTexture(new Uint8Array([128, 128, 255, 255]), 1, 1); normalMap.needsUpdate = true;
    blank = { map, normalMap };
  }
  return blank;
}
let blank: { map: THREE.Texture; normalMap: THREE.Texture } | null = null;

/** Whether a canvas can be drawn here; without one (tests, servers) the pieces keep plain materials. */
export const canDraw = () => typeof document !== 'undefined';

/** Each seed's hammered bump, drawn once: every medallion, rail and plaque of one bronze shares it rather than painting its own. */
const hammered = new Map<number, THREE.CanvasTexture>();

/** The bronze shared by every HUD piece: dark, worn, catching light only at its edges. */
export function bronze(seed = 7, color: THREE.ColorRepresentation = 0x7a5a32): THREE.MeshStandardMaterial {
  let normalMap: THREE.CanvasTexture | null = null;
  if (canDraw()) { normalMap = hammered.get(seed) ?? hammeredNormal(seed, 4); hammered.set(seed, normalMap); }
  return new THREE.MeshStandardMaterial({ color, metalness: .6, roughness: .46, normalMap, normalScale: new THREE.Vector2(.7, .7) });
}
